import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getReport } from '../api/dashboard';
import { errorCode } from '../api/client';
import OffUnitMessage from '../components/OffUnitMessage';
import PageHeader from '../components/PageHeader';
import PhoneFrame from '../components/PhoneFrame';
import PhoneReportScreen from '../components/PhoneReportScreen';
import StatusMessage from '../components/StatusMessage';
import { formatDateTime } from '../utils/format';
import './ReportSheet.css';

const ERROR_MESSAGES = {
  not_found: "This report sheet isn't available anymore. The patient may have been discharged.",
  forbidden: "You don't have access to this report sheet.",
  network_error: "Can't reach the dashboard server. Check your connection.",
};

// Back returns to the page you came from (usually the board, with its scroll
// and search as you left them). Opened directly, e.g. from a bookmark, it goes
// to Report Sheets.
export function useBack(fallback) {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    if (location.key && location.key !== 'default') navigate(-1);
    else navigate(fallback);
  }, [navigate, location.key, fallback]);
}

// A charge nurse's read-only view of one report sheet, opened from the board:
// the Kabilah app's report screen inside an iPhone, so it looks the way nurses
// see it. Nothing can be changed from the dashboard.
const ReportSheet = () => {
  const { reportId } = useParams();
  const clicked = useLocation().state;
  const goBack = useBack('/report-sheets');
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setReport(null);
    try {
      setReport(await getReport(reportId));
    } catch (err) {
      // 401 is handled globally (sign-out).
      if (errorCode(err) !== 'unauthenticated') setError(errorCode(err));
    }
  }, [reportId]);

  useEffect(() => {
    load();
  }, [load]);

  const back = <button type="button" className="back-link" onClick={goBack}>← Back</button>;

  if (error || !report) {
    return (
      <>
        <PageHeader title="Report Sheet" before={back} />
        <div className="component-container">
          <div className="page-content">
            {error === 'sheet_not_on_unit' || (error === 'not_found' && clicked?.patient) ? (
              // The backend says the sheet exists but its room is off the unit
              // (sheet_not_on_unit). A 404 for a patient that was just listed
              // most likely means the same thing.
              <OffUnitMessage patientName={clicked?.patient?.name} room={clicked?.patient?.room} nurseName={clicked?.nurseName} />
            ) : error ? (
              <StatusMessage kind="error" onRetry={error === 'not_found' ? undefined : load}>
                {ERROR_MESSAGES[error] || "We couldn't load this report sheet."}
              </StatusMessage>
            ) : (
              <StatusMessage kind="loading">Loading report sheet…</StatusMessage>
            )}
          </div>
        </div>
      </>
    );
  }

  const { owner } = report;
  const caption = (
    <>
      {owner?.name ? `${owner.name}${owner.isMine ? ' (you)' : ''}'s patient` : 'Unknown nurse'}
      {formatDateTime(report.updatedAt) && <> · Updated {formatDateTime(report.updatedAt)}</>}
    </>
  );

  return (
    <>
      <PageHeader title="Report Sheet" before={back} />
      <div className="component-container phone-page">
        <PhoneFrame label="Report sheet as shown in the Aren app" caption={caption}>
          <PhoneReportScreen report={report} onBack={goBack} backLabel="Back" />
        </PhoneFrame>
      </div>
    </>
  );
};

export default ReportSheet;
